import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
} from '@wundo/ui';
import type { Topic } from '@/types/topicSets.ts';
import type { TopicInput } from '@/lib/topicSets.ts';

export interface TopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The topic being edited; null when a new one is being written. */
  topic: Topic | null;
  saving: boolean;
  /** Shown in the dialog when the last attempt failed; empty while it has not. */
  error: string;
  onSave: (input: TopicInput) => void;
}

/**
 * One theme: a title and what it is about. The same form writes a new topic and
 * edits an existing one, because both ask exactly the same two questions.
 *
 * Both are required — a theme without a description says nothing to the agent
 * that has to use it — so Save stays out of reach until both are filled in.
 */
export function TopicDialog({
  open,
  onOpenChange,
  topic,
  saving,
  error,
  onSave,
}: TopicDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md'>
        {open && (
          <TopicForm
            /* Remounts on another topic, so the fields start from that one. */
            key={topic?.id ?? 'new'}
            topic={topic}
            saving={saving}
            error={error}
            onSave={onSave}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TopicForm({
  topic,
  saving,
  error,
  onSave,
  onCancel,
}: Pick<TopicDialogProps, 'topic' | 'saving' | 'error' | 'onSave'> & {
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(topic?.title ?? '');
  const [description, setDescription] = useState(topic?.description ?? '');
  const submittable = title.trim() !== '' && description.trim() !== '';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{topic ? 'Edit topic' : 'New topic'}</DialogTitle>
      </DialogHeader>

      <div className='flex flex-col gap-4 px-5 py-4'>
        <Field label='Title'>
          {(control) => (
            <Input
              {...control}
              autoFocus
              placeholder='Title'
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
        <Field label='Description'>
          {(control) => (
            <Textarea
              {...control}
              rows={4}
              sizing='manual'
              placeholder='Description'
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        {error !== '' && (
          <p role='alert' className='text-sm text-danger'>
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button size='sm' variant='quiet' disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size='sm'
          disabled={!submittable}
          loading={saving}
          onClick={() =>
            submittable &&
            onSave({ title: title.trim(), description: description.trim() })
          }
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
